import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../services/apiUtils";
import { useAuth } from "../contexts/AuthContext";
import { useDncPreviewAccess } from "../services/dncPreviewAccess";
import { CpaDncWorkspace, DncAuditLog } from "./DncCompliance";
import CanonicalContactsWorkspace from "./leads-contacts/CanonicalContacts";
import {
  DetailGrid,
  DrawerSection,
  EmptyState,
  MetricCard,
  PlaceholderPanel,
  StatusBadge,
  SubTabs,
} from "./leads-contacts/ui";
import {
  adaptApiContact,
  adaptApiLead,
  associateLabel,
  buildProvisionalContacts,
  isLeadDataReady,
  type ApiOverview,
  type ApiContactDetail,
  type ApiProvisionalContact,
  type ApiUnifiedLead,
  type LeadSource,
  type ProvisionalContact,
  type SourceStatus,
  type UnifiedLead,
  buildListingPath,
  buildPublicListingPath,
  attentionLabel,
  calculateSourceShare,
  clearLeadContactFilters,
  contactLinkState,
  isPublicKwHomesListing,
  leadTypeLabel,
  matchOverviewPreset,
  overviewPeriodPresets,
  overviewPeriodRange,
  periodSourceTotal,
  quickViewLabel,
  recordedViewsShare,
  toLocalDateInput,
} from "./leads-contacts/leadAdapters";
import AnalysisWorkspace from "./leads-contacts/AnalysisWorkspace";

type ModuleTab =
  | "overview"
  | "leads"
  | "contacts"
  | "command"
  | "cpa"
  | "compliance"
  | "integrations"
  | "analysis";
type QuickView =
  | "all"
  | "today"
  | "new"
  | "attention"
  | "unmatched-agent"
  | "unmatched-listing"
  | "my-leads";

type SourceState = {
  source: LeadSource;
  status: SourceStatus;
  total: number | null;
  rows: UnifiedLead[];
  error: string | null;
};

const tabs: Array<{ id: ModuleTab; label: string; shortLabel: string }> = [
  { id: "overview", label: "Overview", shortLabel: "Overview" },
  { id: "leads", label: "Leads", shortLabel: "Leads" },
  { id: "contacts", label: "Contacts", shortLabel: "Contacts" },
  { id: "command", label: "Command CRM", shortLabel: "Command" },
  { id: "cpa", label: "CPA / DNC", shortLabel: "CPA / DNC" },
  { id: "compliance", label: "Compliance", shortLabel: "Compliance" },
  { id: "integrations", label: "Integrations", shortLabel: "Integrations" },
];

// Internal KWSA intelligence: shown only to Regional Admin, never beneath operational tabs.
const analysisTab: { id: ModuleTab; label: string; shortLabel: string } = { id: "analysis", label: "Analysis", shortLabel: "Analysis" };

const sourceLabels: Record<LeadSource, string> = {
  PROPERTY24: "Property24",
  PRIVATE_PROPERTY: "Private Property",
  KWHOMES: "KWHomes",
};

const sourceColors: Record<LeadSource, string> = {
  PROPERTY24: "#0f766e",
  PRIVATE_PROPERTY: "#b45309",
  KWHOMES: "#be123c",
};

function formatDate(value: string | null | undefined): string {
  if (!value) return "Not available";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatCompactDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-ZA", { day: "2-digit", month: "short" });
}

function isToday(value: string): boolean {
  const date = new Date(value);
  const now = new Date();
  return date.toDateString() === now.toDateString();
}

function sourceLabelFor(source: LeadSource): string {
  return sourceLabels[source];
}

function ContextSummary({
  role,
  scope,
}: {
  role: string;
  scope: string;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
      <span className="font-semibold text-slate-700">{role}</span>
      <span aria-hidden="true">/</span>
      <span>{scope}</span>
      <StatusBadge label="Read-only data view" tone="info" />
    </div>
  );
}

function SourceBadge({ source }: { source: LeadSource }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700">
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: sourceColors[source] }}
        aria-hidden="true"
      />
      {sourceLabelFor(source)}
    </span>
  );
}

function LeadDetail({
  lead,
  onClose,
  onOpenContact,
  onOpenListing,
  onOpenProperty,
}: {
  lead: UnifiedLead;
  onClose: () => void;
  onOpenContact: () => void;
  onOpenListing: () => void;
  onOpenProperty: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-950/30"
      role="presentation"
      onMouseDown={onClose}
    >
      <aside
        className="h-full w-full max-w-3xl overflow-y-auto bg-white p-5 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-detail-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <SourceBadge source={lead.source} />
              <StatusBadge label="New" tone="info" />
            </div>
            <h2
              id="lead-detail-title"
              className="mt-2 break-words text-xl font-semibold text-slate-900"
            >
              {lead.consumerName ?? "Unknown consumer"}
            </h2>
            <p className="mt-1 break-all text-xs text-slate-500">
              Provider lead ID: {lead.sourceLeadId}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
        </div>

        <div className="mt-5 space-y-5">
          <DrawerSection title="Consumer">
            <DetailGrid
              items={[
                ["Email", lead.email ?? "Not supplied"],
                ["Phone", lead.phone ?? "Not supplied"],
                ["Contact", contactLinkState(lead).label],
                ["Contact linking", "Automatic from email and phone identifiers"],
              ]}
            />
            {lead.provisionalContactId || lead.canonicalContactId ? (
              <button
                type="button"
                onClick={onOpenContact}
                className="mt-3 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                View Contact
              </button>
            ) : (
              <p className="mt-3 text-xs text-slate-500">
                Contact identity unavailable from supplied identifiers.
              </p>
            )}
          </DrawerSection>
          <DrawerSection title="Source">
            <DetailGrid
              items={[
                ["Provider", lead.sourceLabel],
                ["Lead type", lead.leadType ? leadTypeLabel(lead.leadType) : "Not supplied"],
                ["Occurred", formatDate(lead.occurredAt)],
                ["Received", formatDate(lead.receivedAt)],
              ]}
            />
          </DrawerSection>
          <DrawerSection title="Property">
            <DetailGrid
              items={[
                ["Listing", lead.listingReference ?? "Not supplied"],
                [
                  "External reference",
                  lead.listingExternalReference ?? "Not supplied",
                ],
                ["Address", lead.listingAddress ?? "Not supplied"],
                ["Suburb", lead.suburb ?? "Not supplied"],
                ["Listing agent", associateLabel(null, lead.listingAgentKwuid)],
                [
                  "Price",
                  lead.price
                    ? `${lead.price}${lead.priceType ? ` (${lead.priceType})` : ""}`
                    : "Not supplied",
                ],
                ["Type", lead.listingType ?? "Not supplied"],
              ]}
            />
            {isPublicKwHomesListing(
              lead.listingAgentKwuid,
              lead.listingReference,
            ) ? (
              <button
                type="button"
                onClick={onOpenProperty}
                className="mt-3 mr-3 inline-block text-sm font-semibold text-sky-700 hover:underline"
              >
                View Property
              </button>
            ) : null}
            {lead.listingInternalId ? (
              <button
                type="button"
                onClick={onOpenListing}
                className="mt-3 inline-block text-sm font-semibold text-slate-600 hover:underline"
              >
                Open Listing
              </button>
            ) : lead.listingReference ? (
              <p className="mt-3 text-sm text-amber-700">Listing not matched in MAPP</p>
            ) : null}
            {!lead.listingReference && lead.listingUrl ? (
              <a
                className="mt-3 inline-block text-sm font-semibold text-sky-700 hover:underline"
                href={lead.listingUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open Source Listing
              </a>
            ) : null}
          </DrawerSection>
          <DrawerSection title="Assignment">
            <DetailGrid
              items={[
                ["Source agent", associateLabel(lead.agentName, lead.agentKwuid)],
                ["MAPP agent match", lead.agentMatchState],
                ["Agent email", lead.agentEmail ?? "Not supplied"],
                ["Market Centre", lead.marketCentre ?? "Not supplied"],
                ["Team", "Not available from current read facade"],
                ["Listing match", lead.listingMatchState],
              ]}
            />
          </DrawerSection>
          <DrawerSection title="Command">
            <StatusBadge label="Paused" />
            <p className="mt-2 text-sm text-slate-500">
              Command is paused. Once connected, every valid lead assigned to an
              associate syncs automatically to their Command account, with no
              approval step.
            </p>
          </DrawerSection>
          <DrawerSection title="Compliance">
            <DetailGrid
              items={[
                [
                  "Consumer initiated",
                  "Not available from current source endpoint",
                ],
                [
                  "Marketing eligibility",
                  lead.marketingStatus.replace(/_/g, " "),
                ],
                ["DNC", "No MAPP DNC record available"],
                ["Consent", "No MAPP consent record available"],
              ]}
            />
          </DrawerSection>
          <DrawerSection title="Timeline">
            <div className="space-y-3 border-l-2 border-slate-200 pl-4 text-sm text-slate-600">
              <div>
                <span className="font-semibold text-slate-900">Received</span>
                <br />
                {formatDate(lead.receivedAt)}
              </div>
              <div>
                <span className="font-semibold text-slate-900">Occurred</span>
                <br />
                {formatDate(lead.occurredAt)}
              </div>
            </div>
          </DrawerSection>
          <DrawerSection title="Raw Source">
            <p className="text-sm leading-6 text-slate-500">
              Raw payload inspection is restricted to a future privileged
              diagnostic contract.
            </p>
          </DrawerSection>
        </div>
      </aside>
    </div>
  );
}

export default function LeadsContactsPage(): JSX.Element {
  const dncPreviewAccess = useDncPreviewAccess();
  const { token, activeContext, user, isLoading: authLoading, isRegionalAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sourceStates, setSourceStates] = useState<SourceState[]>([
    {
      source: "PRIVATE_PROPERTY",
      status: "CHECKING",
      total: null,
      rows: [],
      error: null,
    },
    {
      source: "PROPERTY24",
      status: "CHECKING",
      total: null,
      rows: [],
      error: null,
    },
    {
      source: "KWHOMES",
      status: "CHECKING",
      total: null,
      rows: [],
      error: null,
    },
  ]);
  const [loading, setLoading] = useState(true);
  const [sourceStatusReady, setSourceStatusReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<UnifiedLead | null>(null);
  const [selectedContact, setSelectedContact] =
    useState<ProvisionalContact | null>(null);
  const [leadParentContact, setLeadParentContact] =
    useState<ProvisionalContact | null>(null);
  const [contactDetail, setContactDetail] = useState<ApiContactDetail | null>(
    null,
  );
  const [contactDetailLoading, setContactDetailLoading] = useState(false);
  const [contactDetailError, setContactDetailError] = useState<string | null>(null);
  const [contactDetailRetry, setContactDetailRetry] = useState(0);
  const [overviewData, setOverviewData] = useState<ApiOverview | null>(null);
  const [leadTotal, setLeadTotal] = useState(0);
  const [, setContactTotal] = useState(0);
  const [leadPageSize, setLeadPageSize] = useState(50);
  const [provisionalContacts, setProvisionalContacts] = useState<
    ProvisionalContact[]
  >([]);
  const [integrationStatus, setIntegrationStatus] = useState<Record<
    string,
    unknown
  > | null>(null);

  const canViewAnalysis = isRegionalAdmin;
  const visibleTabs = [
    ...tabs.filter((tab) => !["cpa", "compliance"].includes(tab.id) || dncPreviewAccess === "allowed"),
    ...(canViewAnalysis ? [analysisTab] : []),
  ];
  const requestedTab = searchParams.get("tab") as ModuleTab | null;
  const activeTab: ModuleTab =
    requestedTab && visibleTabs.some((tab) => tab.id === requestedTab)
      ? requestedTab
      : "overview";
  const search = searchParams.get("q") ?? "";
  const selectedSource = searchParams.get("source") ?? "ALL";
  const contactMatchState = searchParams.get("contactMatchState") ?? "ALL";
  const marketingStatus = searchParams.get("marketingStatus") ?? "ALL";
  const listingMatch = searchParams.get("listingMatch") ?? "ALL";
  const marketCentre = searchParams.get("marketCentre") ?? "";
  const assignedAssociate = searchParams.get("agent") ?? "";
  const quickView = (searchParams.get("view") as QuickView | null) ?? "all";
  const fromDate = searchParams.get("from") ?? "";
  const toDate = searchParams.get("to") ?? "";
  const contactParam = searchParams.get("contact");
  const overviewFrom =
    activeTab === "overview" && !fromDate && !toDate
      ? `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-01`
      : fromDate;
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const pageSize = [25, 50, 100].includes(Number(searchParams.get("pageSize")))
    ? Number(searchParams.get("pageSize"))
    : leadPageSize;
  const sort = searchParams.get("sort") ?? "date";
  const direction = searchParams.get("direction") ?? "desc";

  const setQuery = (key: string, value: string): void => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const setPeriod = (from: string, to: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set("from", from);
    next.set("to", to);
    next.set("page", "1");
    setSearchParams(next, { replace: true });
  };

  const selectTab = (tab: ModuleTab): void => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    if (tab === "overview") {
      ["q", "source", "view", "from", "to"].forEach((key) => next.delete(key));
    }
    if (tab !== "analysis") next.delete("analysisView");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (searchParams.get("identityAnalysis") !== "1") return;
    const next = new URLSearchParams(searchParams);
    next.delete("identityAnalysis");
    next.delete("review");
    next.set("tab", "analysis");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const leadQuery = useMemo(() => {
    const params = new URLSearchParams({
      limit: String(pageSize),
      offset: String((page - 1) * pageSize),
      sort,
      direction,
    });
    if (search.trim()) params.set("search", search.trim());
    if (selectedSource !== "ALL") params.set("source", selectedSource);
    if (contactMatchState !== "ALL") params.set("contactMatchState", contactMatchState);
    if (marketingStatus !== "ALL") params.set("marketingStatus", marketingStatus);
    if (listingMatch !== "ALL") params.set("listingMatch", listingMatch);
    if (marketCentre.trim()) params.set("marketCentre", marketCentre.trim());
    if (assignedAssociate.trim()) params.set("agent", assignedAssociate.trim());
    if (fromDate) params.set("from", fromDate);
    if (toDate) params.set("to", toDate);
    if (quickView === "today" || quickView === "new")
      params.set("quickView", quickView);
    if (
      quickView === "attention" ||
      quickView === "unmatched-agent" ||
      quickView === "unmatched-listing"
    )
      params.set("attention", quickView);
    return params.toString();
  }, [
    direction,
    fromDate,
    page,
    pageSize,
    quickView,
    contactMatchState,
    marketingStatus,
    listingMatch,
    marketCentre,
    assignedAssociate,
    search,
    selectedSource,
    sort,
    toDate,
  ]);

  useEffect(() => {
    if (!isLeadDataReady(authLoading, token, activeContext?.id ?? null)) {
      setLoading(Boolean(token));
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSourceStatusReady(false);
    setLoadError(null);
    const headers = {
      Authorization: `Bearer ${token}`,
      "X-Active-Context": activeContext?.id ?? "",
    };
    const fetchJson = async <T,>(
      endpoint: string,
    ): Promise<{ ok: boolean; body: T & { error?: string } }> => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await apiFetch(endpoint, {
          headers,
          signal: controller.signal,
        });
        const body = (await response.json().catch(() => ({}))) as T & {
          error?: string;
        };
        return {
          ok: response.ok,
          body: response.ok
            ? body
            : { ...body, error: body.error ?? `HTTP ${response.status}` },
        };
      } catch (error) {
        return {
          ok: false,
          body: {
            error:
              error instanceof DOMException && error.name === "AbortError"
                ? "Request timed out."
                : error instanceof Error
                  ? error.message
                  : "Request failed.",
          } as T & { error: string },
        };
      } finally {
        window.clearTimeout(timeout);
      }
    };
    const needsLeads = activeTab === "leads";
    const needsOverview = activeTab === "overview";
    const needsContacts = false;
    const needsIntegrations =
      activeTab === "overview" ||
      activeTab === "leads" ||
      activeTab === "contacts" ||
      activeTab === "integrations";
    void Promise.all([
      needsLeads
        ? fetchJson<{
            rows?: ApiUnifiedLead[];
            total?: number;
            sourceAvailability?: Record<LeadSource, SourceStatus>;
          }>(`/api/leads-contacts/leads?${leadQuery}`)
        : Promise.resolve(null),
      needsOverview
        ? fetchJson<ApiOverview>(
            `/api/leads-contacts/overview?${new URLSearchParams({ ...(overviewFrom ? { from: overviewFrom } : {}), ...(toDate ? { to: toDate } : {}) }).toString()}`,
          )
        : Promise.resolve(null),
      needsContacts
        ? fetchJson<{ rows?: ApiProvisionalContact[]; total?: number }>(
            `/api/leads-contacts/contacts?${new URLSearchParams({ limit: String(pageSize), offset: String((page - 1) * pageSize), ...(search ? { search } : {}), ...(selectedSource !== "ALL" ? { source: selectedSource } : {}), ...(fromDate ? { from: fromDate } : {}), ...(toDate ? { to: toDate } : {}) }).toString()}`,
          )
        : Promise.resolve(null),
      needsIntegrations
        ? fetchJson<Record<string, unknown>>(
            "/api/leads-contacts/integrations/status",
          )
        : Promise.resolve(null),
    ])
      .then(
        ([
          leadsResponse,
          overviewResponse,
          contactsResponse,
          integrationsResponse,
        ]) => {
          if (cancelled) return;
          if (leadsResponse && !leadsResponse.ok) {
            setLoadError(
              leadsResponse.body.error ?? "Unified leads could not be loaded.",
            );
            setSourceStates((current) =>
              current.map((state) => ({
                ...state,
                status: "UNAVAILABLE",
                error: leadsResponse.body.error ?? "Unified API unavailable.",
              })),
            );
          } else if (leadsResponse?.ok) {
            const rows = (leadsResponse.body.rows ?? []).map(adaptApiLead);
            const availability = (leadsResponse.body.sourceAvailability ??
              {}) as Partial<
              Record<
                LeadSource,
                "AVAILABLE" | "UNAVAILABLE" | "DISABLED" | "UNKNOWN"
              >
            >;
            const bySource = (
              ["PRIVATE_PROPERTY", "PROPERTY24", "KWHOMES"] as LeadSource[]
            ).map((source) => ({
              source,
              status:
                availability[source] === "AVAILABLE"
                  ? "CONNECTED"
                  : ("UNAVAILABLE" as SourceStatus),
              total: null,
              rows: rows.filter((row) => row.source === source),
              error:
                availability[source] === "DISABLED"
                  ? "Disabled by current application state."
                  : null,
            }));
            setSourceStates(bySource);
            setLeadTotal(leadsResponse.body.total ?? 0);
            setLoadError(null);
          }
          if (overviewResponse?.ok) setOverviewData(overviewResponse.body);
          if (contactsResponse?.ok) {
            setProvisionalContacts(
              (contactsResponse.body.rows ?? []).map(adaptApiContact),
            );
            setContactTotal(contactsResponse.body.total ?? 0);
          }
          if (integrationsResponse?.ok) {
            setIntegrationStatus(integrationsResponse.body);
            const integrationSources: Array<
              [LeadSource, "property24" | "privateProperty" | "kwhomes"]
            > = [
              ["PROPERTY24", "property24"],
              ["PRIVATE_PROPERTY", "privateProperty"],
              ["KWHOMES", "kwhomes"],
            ];
            setSourceStates((current) =>
              current.map((state) => {
                const entry = integrationSources.find(
                  ([source]) => source === state.source,
                );
                const status = entry
                  ? (integrationsResponse.body[entry[1]] as
                      { state?: string; total?: number } | undefined)
                  : undefined;
                return {
                  ...state,
                  status:
                    status?.state === "AVAILABLE"
                      ? "CONNECTED"
                      : status?.state === "DISABLED"
                        ? "UNAVAILABLE"
                        : "UNAVAILABLE",
                  total: status?.total ?? state.total,
                  error:
                    status?.state === "DISABLED"
                      ? "Disabled by current application state."
                      : null,
                };
              }),
            );
          }
          const secondaryResponses = [
            overviewResponse,
            contactsResponse,
            integrationsResponse,
          ].filter((result): result is NonNullable<typeof result> =>
            Boolean(result),
          );
          const secondaryErrors = secondaryResponses
            .filter((result) => !result.ok)
            .map((result) => result.body.error)
            .filter(Boolean);
          if (secondaryErrors.length > 0 && leadsResponse && !leadsResponse.ok)
            setLoadError(secondaryErrors.join(" "));
          setSourceStatusReady(true);
        },
      )
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    activeContext,
    activeTab,
    authLoading,
    fromDate,
    leadQuery,
    overviewFrom,
    page,
    pageSize,
    search,
    selectedSource,
    toDate,
    token,
  ]);

  useEffect(() => {
    if (!selectedContact || !token || !activeContext?.id) {
      setContactDetail(null);
      setContactDetailLoading(false);
      setContactDetailError(null);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setContactDetail(null);
    setContactDetailLoading(true);
    setContactDetailError(null);
    void apiFetch(
      selectedContact.id.startsWith("candidate:")
        ? `/api/leads-contacts/contacts/candidate/${selectedContact.id.replace(/^candidate:/, "")}`
        : `/api/leads-contacts/contacts/${selectedContact.id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Active-Context": activeContext.id,
        },
        signal: controller.signal,
      },
    )
      .then(async (response) => {
        if (!response.ok) {
          const body = (await response.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `Unable to load contact details (${response.status})`);
        }
        if (cancelled) return;
        const body = (await response.json()) as {
          row: ApiProvisionalContact;
          enquiries: ApiUnifiedLead[];
          associatedAgents: ApiContactDetail["associatedAgents"];
        };
        if (!cancelled)
          setContactDetail({
            ...body.row,
            enquiries: body.enquiries,
            associatedAgents: body.associatedAgents,
          });
      })
      .catch((error) => {
        if (!cancelled) setContactDetailError(error instanceof Error ? error.message : "Error loading section");
      })
      .finally(() => {
        if (!cancelled) setContactDetailLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [activeContext?.id, contactDetailRetry, selectedContact, token]);

  useEffect(() => {
    if (!contactParam || selectedContact || selectedLead) return;
    const existing = provisionalContacts.find(
      (contact) => contact.id === `candidate:${contactParam}`,
    );
    setSelectedContact(
      existing ?? {
        id: `candidate:${contactParam}`,
        name: "Contact",
        email: null,
        phone: null,
        owner: null,
        marketCentre: null,
        sources: [],
        enquiryCount: 0,
        lastActivity: "",
        leadIds: [],
        provisional: true,
      },
    );
  }, [contactParam, provisionalContacts, selectedContact, selectedLead]);

  const allLeads = useMemo(
    () => sourceStates.flatMap((state) => state.rows),
    [sourceStates],
  );
  const filteredLeads = allLeads;

  const roleLabel = activeContext?.role ?? user?.role ?? "Authenticated user";
  const scopeLabel = activeContext?.marketCenter
    ? activeContext.marketCenter
    : (activeContext?.label ?? "Current scope");
  const knownTotal = sourceStates.reduce(
    (total, state) => total + (state.total ?? 0),
    0,
  );
  const connectedSources = sourceStates.filter(
    (state) => state.status === "CONNECTED",
  );
  const attentionCount = overviewData?.metrics.needsAttentionLeads ?? null;
  const todayCount = allLeads.filter((lead) =>
    isToday(lead.occurredAt ?? lead.receivedAt),
  ).length;
  const openLeads = (source?: LeadSource): void => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "leads");
    if (source) next.set("source", source);
    setSearchParams(next, { replace: true });
  };
  const openContactForLead = (lead: UnifiedLead): void => {
    if (lead.canonicalContactId) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", "contacts");
      next.set("contactId", lead.canonicalContactId);
      next.delete("contact");
      setSelectedLead(null);
      setSearchParams(next, { replace: true });
      return;
    }
    if (!lead.provisionalContactId) return;
    setSelectedContact({
      id: `candidate:${lead.provisionalContactId}`,
      name: lead.consumerName ?? "Unknown consumer",
      email: lead.email,
      phone: lead.phone,
      owner: associateLabel(lead.agentName, lead.agentKwuid),
      marketCentre: lead.marketCentre,
      sources: [lead.source],
      enquiryCount: 1,
      lastActivity: lead.occurredAt ?? lead.receivedAt,
      leadIds: [lead.id],
      provisional: true,
    });
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("tab", activeTab);
        next.set("contact", lead.provisionalContactId as string);
        return next;
      },
      { replace: true },
    );
  };
  const openListing = (listingId: string): void =>
    navigate(buildListingPath(listingId));
  const openPublicListing = (
    listingAgentKwuid: string,
    listingNumber: string,
  ): void => {
    window.open(
      buildPublicListingPath(listingAgentKwuid, listingNumber),
      "_blank",
      "noopener,noreferrer",
    );
  };
  const closeContact = (): void => {
    setSelectedContact(null);
    setContactDetail(null);
    setContactDetailError(null);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("contact");
        return next;
      },
      { replace: true },
    );
  };
  const filterKeys = [
    "q",
    "source",
    "view",
    "from",
    "to",
    "marketCentre",
    "team",
    "agent",
    "leadType",
    "saleOrRent",
    "city",
    "suburb",
    "priceMin",
    "priceMax",
    "listingNumber",
    "agentMatch",
    "listingMatch",
    "contactMatchState",
    "marketingStatus",
  ];
  const filtersActive =
    filterKeys.some((key) => Boolean(searchParams.get(key))) ||
    sort !== "date" ||
    direction !== "desc" ||
    page > 1;
  const clearFilters = (): void => {
    setSearchParams((current) => clearLeadContactFilters(current), {
      replace: true,
    });
  };

  return (
    <div className="space-y-4">
      <header className="surface-card p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full bg-rose-600"
                aria-hidden="true"
              />
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                MAPP workspace
              </span>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Leads &amp; Contacts
            </h1>
            <div className="mt-2">
              <ContextSummary role={roleLabel} scope={scopeLabel} />
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Data sources:</span>
            <span className="font-semibold text-slate-700">
              {sourceStatusReady
                ? `${connectedSources.length} of 3 available`
                : authLoading || loading
                  ? "Checking..."
                  : "Status unavailable"}
            </span>
          </div>
        </div>
      </header>

      {authLoading || (token && !activeContext) ? (
        <section className="surface-card p-6" role="status" aria-live="polite">
          <div className="text-sm font-semibold text-slate-800">
            Restoring your authenticated MAPP context...
          </div>
          <div className="mt-1 text-sm text-slate-500">
            Lead and contact requests will begin after the session and scope are
            ready.
          </div>
        </section>
      ) : null}

      <nav
        className="surface-card overflow-x-auto p-2"
        aria-label="Leads and Contacts sections"
      >
        <div className="flex min-w-max gap-1" role="tablist">
          {visibleTabs.map((tab) => (
            <Fragment key={tab.id}>
              {tab.id === "analysis" ? (
                <span className="mx-1 my-1.5 w-px bg-slate-200" aria-hidden="true" />
              ) : null}
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => selectTab(tab.id)}
                className={`rounded-md px-3 py-2 text-sm font-semibold transition ${activeTab === tab.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}
              >
                {tab.label}
              </button>
            </Fragment>
          ))}
        </div>
      </nav>

      {activeTab === "overview" ? (
        <Overview
          loading={loading}
          loadError={loadError}
          sourceStates={sourceStates}
          allLeads={allLeads}
          overviewData={overviewData}
          knownTotal={knownTotal}
          todayCount={overviewData?.metrics.leadsToday ?? todayCount}
          attentionCount={attentionCount}
          fromDate={overviewFrom}
          toDate={toDate}
          onPeriod={setPeriod}
          onOpenLeads={openLeads}
          onSelectLead={setSelectedLead}
          filtersActive={filtersActive}
          onClearFilters={clearFilters}
        />
      ) : null}
      {activeTab === "leads" ? (
        <LeadsWorkspace
          loading={loading}
          loadError={loadError}
          leads={filteredLeads}
          total={leadTotal}
          page={page}
          pageSize={pageSize}
          sort={sort}
          direction={direction}
          search={search}
          source={selectedSource}
          quickView={quickView}
          fromDate={fromDate}
          toDate={toDate}
          onSearch={(value) => setQuery("q", value)}
          onSource={(value) => setQuery("source", value)}
          contactMatchState={contactMatchState}
          marketingStatus={marketingStatus}
          listingMatch={listingMatch}
          marketCentre={marketCentre}
          assignedAssociate={assignedAssociate}
          onContactMatchState={(value) => setQuery("contactMatchState", value)}
          onMarketingStatus={(value) => setQuery("marketingStatus", value)}
          onListingMatch={(value) => setQuery("listingMatch", value)}
          onMarketCentre={(value) => setQuery("marketCentre", value)}
          onAssignedAssociate={(value) => setQuery("agent", value)}
          onQuickView={(value) => setQuery("view", value)}
          onFrom={(value) => setQuery("from", value)}
          onTo={(value) => setQuery("to", value)}
          onPage={(value) => setQuery("page", String(value))}
          onPageSize={(value) => {
            setLeadPageSize(value);
            setQuery("pageSize", String(value));
            setQuery("page", "1");
          }}
          onSort={(value) => {
            setQuery("sort", value);
            setQuery("page", "1");
          }}
          onDirection={(value) => {
            setQuery("direction", value);
            setQuery("page", "1");
          }}
          onSelectLead={setSelectedLead}
          onOpenContact={openContactForLead}
          onOpenListing={(lead) => {
            if (lead.listingAgentKwuid && lead.listingReference)
              openPublicListing(lead.listingAgentKwuid, lead.listingReference);
          }}
          onOpenMappListing={(lead) => {
            if (lead.listingInternalId) openListing(lead.listingInternalId);
          }}
          filtersActive={filtersActive}
          onClearFilters={clearFilters}
        />
      ) : null}
      {activeTab === "contacts" ? (
        <CanonicalContactsWorkspace />
      ) : null}
      {activeTab === "command" ? (
        <PausedCommandWorkspace />
      ) : null}
      {activeTab === "cpa" ? <CpaDncWorkspace /> : null}
      {activeTab === "compliance" ? <ComplianceWorkspace /> : null}
      {activeTab === "integrations" ? (
        <IntegrationsWorkspace
          sourceStates={sourceStates}
          integrationStatus={integrationStatus}
        />
      ) : null}
      {activeTab === "analysis" && canViewAnalysis ? <AnalysisWorkspace /> : null}

      {selectedLead ? (
        <LeadDetail
          lead={selectedLead}
          onClose={() => {
            setSelectedLead(null);
            if (leadParentContact) {
              setSelectedContact(leadParentContact);
              setLeadParentContact(null);
            }
          }}
          onOpenContact={() => {
            if (selectedLead) {
              setSelectedLead(null);
              openContactForLead(selectedLead);
            }
          }}
          onOpenListing={() => {
            if (selectedLead?.listingInternalId)
              openListing(selectedLead.listingInternalId);
          }}
          onOpenProperty={() => {
            if (
              selectedLead?.listingAgentKwuid &&
              selectedLead.listingReference
            )
              openPublicListing(
                selectedLead.listingAgentKwuid,
                selectedLead.listingReference,
              );
          }}
        />
      ) : null}
      {selectedContact ? (
        <ContactProfile
          contact={selectedContact}
          onClose={closeContact}
          detail={contactDetail}
          loading={contactDetailLoading}
          error={contactDetailError}
          onRetry={() => setContactDetailRetry((value) => value + 1)}
          onSelectLead={(lead) => {
            if (selectedContact) setLeadParentContact(selectedContact);
            setSelectedContact(null);
            setSelectedLead(lead);
          }}
        />
      ) : null}
    </div>
  );
}

function Overview({
  loading,
  loadError,
  sourceStates,
  allLeads,
  overviewData,
  knownTotal,
  todayCount,
  attentionCount,
  fromDate,
  toDate,
  onPeriod,
  onOpenLeads,
  onSelectLead,
  filtersActive,
  onClearFilters,
}: {
  loading: boolean;
  loadError: string | null;
  sourceStates: SourceState[];
  allLeads: UnifiedLead[];
  overviewData: ApiOverview | null;
  knownTotal: number;
  todayCount: number;
  attentionCount: number | null;
  fromDate: string;
  toDate: string;
  onPeriod: (from: string, to: string) => void;
  onOpenLeads: (source?: LeadSource) => void;
  onSelectLead: (lead: UnifiedLead) => void;
  filtersActive: boolean;
  onClearFilters: () => void;
}): JSX.Element {
  const uniqueContacts =
    overviewData?.metrics.uniqueProvisionalContacts ??
    buildProvisionalContacts(allLeads).length;
  const periodTotal =
    overviewData?.bySource.reduce((sum, item) => sum + (Number(item.total) || 0), 0) ?? 0;
  const activePreset = matchOverviewPreset(fromDate, toDate);
  const periodLabel = formatPeriodLabel(fromDate, toDate);
  return (
    <div className="space-y-4">
      <section className="surface-card flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            Activity period
          </h2>
          <p className="text-xs text-slate-500">
            {periodLabel} · Known leads is an all-time stored total.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="inline-flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1"
            role="group"
            aria-label="Overview period"
          >
            {overviewPeriodPresets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                aria-pressed={activePreset === preset.id}
                onClick={() => {
                  const range = overviewPeriodRange(preset.id);
                  onPeriod(range.from, range.to);
                }}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${activePreset === preset.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-500">
            From
            <input type="date" aria-label="Activity period from" value={fromDate} max={toDate || undefined}
              onChange={(event) => onPeriod(event.target.value, toDate)} className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-slate-700" />
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-500">
            To
            <input type="date" aria-label="Activity period to" value={toDate} min={fromDate || undefined}
              onChange={(event) => onPeriod(fromDate, event.target.value)} className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-slate-700" />
          </label>
          {filtersActive ? (
            <button
              type="button"
              onClick={onClearFilters}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Clear Filters
            </button>
          ) : null}
        </div>
      </section>
      {loadError ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {loadError} The rest of the module remains available as a read-only
          shell.
        </div>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <MetricCard
          label="Known leads"
          value={loading ? "..." : knownTotal.toLocaleString()}
          detail="All-time, all connected sources"
        />
        <MetricCard
          label="Leads in period"
          value={loading ? "..." : overviewData ? periodTotal.toLocaleString() : "--"}
          detail={periodLabel}
        />
        <MetricCard
          label="Leads today"
          value={loading ? "..." : todayCount.toLocaleString()}
          detail="Today, South African time"
        />
        <MetricCard
          label="Provisional contacts"
          value={loading ? "..." : uniqueContacts ? uniqueContacts.toLocaleString() : "--"}
          detail="Distinct email and phone pairs in period"
        />
        <MetricCard
          label="Needs attention"
          value={loading ? "..." : (attentionCount ?? "--")}
          detail={
            attentionCount === null
              ? "Not evaluated for this scope"
              : "Unique leads with actionable issues"
          }
          tone={attentionCount && attentionCount > 0 ? "warning" : "default"}
        />
      </div>
      <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <section className="surface-card p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Lead Source Mix
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Share of leads received in the selected period.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onOpenLeads()}
              className="text-xs font-semibold text-sky-700 hover:underline"
            >
              Open Leads
            </button>
          </div>
          <div className="mt-5 space-y-3">
            {sourceStates.map((state) => (
              <SourceSummary
                key={state.source}
                state={state}
                total={
                  overviewData
                    ? periodSourceTotal(overviewData.bySource, state.source)
                    : state.total
                }
                periodTotal={periodTotal}
                historicalTotal={state.total}
                hasPeriod={Boolean(overviewData)}
                onOpenSource={() => onOpenLeads(state.source)}
              />
            ))}
          </div>
        </section>
        <section className="surface-card p-5">
          <h2 className="text-lg font-semibold text-slate-900">Lead Trend</h2>
          <p className="mt-1 text-sm text-slate-500">
            Source-only daily activity for the selected period.
          </p>
          <div className="mt-5">
            {overviewData?.trend.length ? (
              <TrendChart trend={overviewData.trend} />
            ) : (
              <EmptyState
                title="Trend data is not available"
                detail="No source activity was returned for this period."
              />
            )}
          </div>
        </section>
      </div>
      <ViewsAnalytics overviewData={overviewData} loading={loading} />
      {attentionCount !== null ? (
        <AttentionQueue
          leads={allLeads}
          evaluated
          onSelectLead={onSelectLead}
        />
      ) : null}
    </div>
  );
}

function formatPeriodLabel(from: string, to: string): string {
  const format = (value: string): string => {
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
  };
  if (!from && !to) return "All time";
  if (from && !to) return `${format(from)} – ${format(toLocalDateInput(new Date()))}`;
  if (!from) return `Up to ${format(to)}`;
  return from === to ? format(from) : `${format(from)} – ${format(to)}`;
}

export function ViewsAnalytics({ overviewData, loading }: { overviewData: ApiOverview | null; loading: boolean }): JSX.Element {
  const mix = overviewData?.viewsSourceMix;
  return (
    <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
      <section className="surface-card p-5">
        <h2 className="text-lg font-semibold text-slate-900">Views Source Mix</h2>
        <p className="mt-1 text-sm text-slate-500" title="View percentages are based on recorded portal view data available for the selected period.">
          Share of recorded views in the selected period.
        </p>
        <div className="mt-5 space-y-3">
          {(["PRIVATE_PROPERTY", "PROPERTY24", "KWHOMES"] as LeadSource[]).map((source) => {
            const total = mix?.[source] ?? null;
            const share = recordedViewsShare(total, mix);
            return (
              <div key={source} className="rounded-lg border border-slate-200 px-3 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <SourceBadge source={source} />
                    <div className="mt-1 text-xs text-slate-500">{loading ? "Loading..." : total === null ? "Not available" : `${total.toLocaleString()} views in period`}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold tabular-nums text-slate-900">{loading || share === null ? "--" : `${share.toFixed(1)}%`}</div>
                    <div className="text-[11px] text-slate-500">of recorded views</div>
                  </div>
                </div>
                {total !== null && !loading ? (
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full" style={{ width: `${share ?? 0}%`, backgroundColor: sourceColors[source] }} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>
      <section className="surface-card p-5">
        <h2 className="text-lg font-semibold text-slate-900">Views Trend</h2>
        <p className="mt-1 text-sm text-slate-500">Recorded daily portal views for the selected period.</p>
        <div className="mt-5">
          {loading ? <EmptyState title="Loading views..." detail="" /> : overviewData?.viewsTrend?.some((day) => day.property24 !== null || day.privateProperty !== null || day.kwhomes !== null) ? (
            <TrendChart trend={[]} viewsTrend={overviewData.viewsTrend} />
          ) : <EmptyState title="Views data is not available" detail="No recorded views were returned for this period." />}
        </div>
      </section>
    </div>
  );
}

function TrendChart({ trend, viewsTrend }: { trend: ApiOverview["trend"]; viewsTrend?: ApiOverview["viewsTrend"] }): JSX.Element {
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const dates = [...new Set(trend.map((item) => item.date))].sort();
  const values = viewsTrend ? viewsTrend.map((day) => ({ date: day.date, PROPERTY24: day.property24, PRIVATE_PROPERTY: day.privateProperty, KWHOMES: day.kwhomes })) : dates.map((date) => {
    const bySource = new Map(
      trend
        .filter((item) => item.date === date)
        .map((item) => [item.source, item.total]),
    );
    return {
      date,
      PROPERTY24: bySource.get("PROPERTY24") ?? 0,
      PRIVATE_PROPERTY: bySource.get("PRIVATE_PROPERTY") ?? 0,
      KWHOMES: bySource.get("KWHOMES") ?? 0,
    };
  });
  if (!viewsTrend && dates.length <= 1) {
    const value = values[0];
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="text-xs text-slate-500">{value?.date ?? "Today"}</div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {(["PRIVATE_PROPERTY", "PROPERTY24", "KWHOMES"] as LeadSource[]).map(
            (source) => (
              <div key={source}>
                <div className="text-[11px] text-slate-500">
                  {sourceLabelFor(source)}
                </div>
                <div className="text-lg font-semibold tabular-nums text-slate-900">
                  {value?.[source] ?? 0}
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    );
  }
  const maxValue = Math.max(
    1,
    ...values.flatMap((value) => [
      value.PRIVATE_PROPERTY,
      value.PROPERTY24,
      value.KWHOMES,
    ].filter((count): count is number => count !== null)),
  );
  const width = 640;
  const height = 220;
  const x = (index: number) =>
    42 + (index * (width - 62)) / Math.max(1, values.length - 1);
  const y = (value: number) => height - 30 - (value / maxValue) * (height - 55);
  const line = (source: LeadSource) => {
    let connected = false;
    return values.map((value, index) => {
      const count = value[source];
      if (count === null) { connected = false; return ""; }
      const point = `${connected ? "L" : "M"}${x(index)},${y(count)}`;
      connected = true;
      return point;
    }).join(" ");
  };
  const selected = values.find((value) => value.date === activeDate);
  const tooltip = (value: typeof values[number]) => `${value.date}\n${(["PRIVATE_PROPERTY", "PROPERTY24", "KWHOMES"] as LeadSource[]).map((source) => `${sourceLabelFor(source)} views: ${value[source] === null ? "Not available" : value[source]}`).join("\n")}`;
  return (
    <div className="relative rounded-lg border border-slate-200 bg-white p-3">
      {selected && viewsTrend ? <div role="tooltip" className="pointer-events-none absolute right-3 top-10 z-10 whitespace-pre-line rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-sm">{tooltip(selected)}</div> : null}
      <div className="mb-2 flex flex-wrap gap-3 text-xs">
        {(["PRIVATE_PROPERTY", "PROPERTY24", "KWHOMES"] as LeadSource[]).map(
          (source) => (
            <span
              key={source}
              className="inline-flex items-center gap-1.5 text-slate-600"
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: sourceColors[source] }}
              />
              {sourceLabelFor(source)}
            </span>
          ),
        )}
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-52 w-full"
        role="img"
        aria-label={viewsTrend ? "Views trend by source" : "Lead trend by source"}
      >
        <line x1="42" y1="10" x2="42" y2={height - 30} stroke="#cbd5e1" />
        <line
          x1="42"
          y1={height - 30}
          x2={width - 20}
          y2={height - 30}
          stroke="#cbd5e1"
        />
        {(["PRIVATE_PROPERTY", "PROPERTY24", "KWHOMES"] as LeadSource[]).map(
          (source) => (
            <path
              key={source}
              d={line(source)}
              fill="none"
              stroke={sourceColors[source]}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <title>{sourceLabelFor(source)}</title>
            </path>
          ),
        )}
        {values.filter((_value, index) => index === 0 || index === values.length - 1 || index % Math.max(1, Math.ceil(values.length / 7)) === 0).map((value) => (
          <text
            key={value.date}
            x={x(values.indexOf(value))}
            y={height - 10}
            textAnchor="middle"
            className="fill-slate-500 text-[10px]"
          >
            {value.date.slice(5)}
          </text>
        ))}
        {values.map((value, index) => (
          <g key={`${value.date}-points`}>
            {(
              ["PRIVATE_PROPERTY", "PROPERTY24", "KWHOMES"] as LeadSource[]
            ).map((source) => value[source] === null ? null : (
              <circle
                key={source}
                cx={x(index)}
                cy={y(value[source] as number)}
                r="4"
                fill={sourceColors[source]}
              >
                <title>{`${sourceLabelFor(source)} · ${value.date}: ${value[source]}`}</title>
              </circle>
            ))}
            {viewsTrend ? <rect x={x(index) - (width - 62) / Math.max(1, values.length) / 2} y="0" width={(width - 62) / Math.max(1, values.length)} height={height - 25} fill="transparent" tabIndex={0}
              aria-label={tooltip(value)} onMouseEnter={() => setActiveDate(value.date)} onMouseLeave={() => setActiveDate(null)}
              onFocus={() => setActiveDate(value.date)} onBlur={() => setActiveDate(null)} onClick={() => setActiveDate(value.date)} /> : null}
          </g>
        ))}
      </svg>
    </div>
  );
}

function SourceSummary({
  state,
  total,
  periodTotal,
  historicalTotal,
  hasPeriod,
  onOpenSource,
}: {
  state: SourceState;
  total: number | null;
  periodTotal: number;
  historicalTotal: number | null;
  hasPeriod: boolean;
  onOpenSource: () => void;
}): JSX.Element {
  if (state.status === "CHECKING")
    return (
      <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-3">
        <SourceBadge source={state.source} />
        <StatusBadge label="Checking..." tone="info" />
      </div>
    );
  if (state.status !== "CONNECTED")
    return (
      <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-3">
        <SourceBadge source={state.source} />
        <StatusBadge label="Unavailable" />
      </div>
    );
  const share = hasPeriod ? calculateSourceShare(total ?? 0, periodTotal) : null;
  return (
    <button
      type="button"
      onClick={onOpenSource}
      className="block w-full rounded-lg border border-slate-200 px-3 py-3 text-left transition hover:border-slate-300 hover:bg-slate-50"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <SourceBadge source={state.source} />
          <div className="mt-1 text-xs text-slate-500">
            {hasPeriod
              ? `${(total ?? 0).toLocaleString()} leads in period`
              : `${(historicalTotal ?? total ?? 0).toLocaleString()} stored leads`}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold tabular-nums text-slate-900">
            {hasPeriod
              ? share === null
                ? "--"
                : `${share.toFixed(1)}%`
              : (historicalTotal ?? total ?? "--")}
          </div>
          <div className="text-[11px] text-slate-500">
            {hasPeriod ? "of period" : "stored historically"}
          </div>
        </div>
      </div>
      {hasPeriod ? (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full"
            style={{ width: `${share ?? 0}%`, backgroundColor: sourceColors[state.source] }}
          />
        </div>
      ) : null}
    </button>
  );
}

function AttentionQueue({
  leads,
  evaluated,
  onSelectLead,
}: {
  leads: UnifiedLead[];
  evaluated: boolean;
  onSelectLead: (lead: UnifiedLead) => void;
}): JSX.Element {
  const items = leads.filter((lead) => lead.attention.length > 0).slice(0, 8);
  return (
    <section className="surface-card p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Needs attention
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Actionable issues derived from the current readable lead set.
          </p>
        </div>
        <StatusBadge
          label={
            !evaluated ? "--" : items.length ? `${items.length} items` : "0"
          }
          tone={items.length ? "warning" : "good"}
        />
      </div>
      {items.length ? (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-2">Category</th>
                <th className="px-2 py-2">Item</th>
                <th className="px-2 py-2">Source</th>
                <th className="px-2 py-2">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((lead) => (
                <tr key={lead.id}>
                  <td className="px-2 py-3">
                    <StatusBadge
                      label={attentionLabel(lead.attention[0])}
                      tone="warning"
                    />
                  </td>
                  <td className="px-2 py-3 font-medium text-slate-800">
                    {lead.consumerName ?? "Unknown consumer"}
                  </td>
                  <td className="px-2 py-3">
                    <SourceBadge source={lead.source} />
                  </td>
                  <td className="px-2 py-3">
                    <button
                      type="button"
                      onClick={() => onSelectLead(lead)}
                      className="font-semibold text-sky-700 hover:underline"
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !evaluated ? (
        <div className="mt-4">
          <EmptyState
            title="Attention not evaluated"
            detail="This overview does not run historical enrichment for attention metrics."
          />
        </div>
      ) : (
        <div className="mt-4">
          <EmptyState
            title="No actionable issues found"
            detail="No readable lead currently requires attention under the active matching rules."
          />
        </div>
      )}
    </section>
  );
}

function LeadsWorkspace({
  loading,
  loadError,
  leads,
  total,
  page,
  pageSize,
  sort,
  direction,
  search,
  source,
  contactMatchState,
  marketingStatus,
  listingMatch,
  marketCentre,
  assignedAssociate,
  quickView,
  fromDate,
  toDate,
  onSearch,
  onSource,
  onContactMatchState,
  onMarketingStatus,
  onListingMatch,
  onMarketCentre,
  onAssignedAssociate,
  onQuickView,
  onFrom,
  onTo,
  onPage,
  onPageSize,
  onSort,
  onDirection,
  onSelectLead,
  onOpenContact,
  onOpenListing,
  onOpenMappListing,
  filtersActive,
  onClearFilters,
}: {
  loading: boolean;
  loadError: string | null;
  leads: UnifiedLead[];
  total: number;
  page: number;
  pageSize: number;
  sort: string;
  direction: string;
  search: string;
  source: string;
  quickView: QuickView;
  fromDate: string;
  toDate: string;
  onSearch: (value: string) => void;
  onSource: (value: string) => void;
  contactMatchState: string;
  marketingStatus: string;
  listingMatch: string;
  marketCentre: string;
  assignedAssociate: string;
  onContactMatchState: (value: string) => void;
  onMarketingStatus: (value: string) => void;
  onListingMatch: (value: string) => void;
  onMarketCentre: (value: string) => void;
  onAssignedAssociate: (value: string) => void;
  onQuickView: (value: QuickView) => void;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
  onPage: (value: number) => void;
  onPageSize: (value: number) => void;
  onSort: (value: string) => void;
  onDirection: (value: string) => void;
  onSelectLead: (lead: UnifiedLead) => void;
  onOpenContact: (lead: UnifiedLead) => void;
  onOpenListing: (lead: UnifiedLead) => void;
  onOpenMappListing: (lead: UnifiedLead) => void;
  filtersActive: boolean;
  onClearFilters: () => void;
}): JSX.Element {
  const secondaryActive = [marketCentre, assignedAssociate].some(Boolean) ||
    [contactMatchState, marketingStatus, listingMatch].some((value) => value !== "ALL");
  const [showMoreFilters, setShowMoreFilters] = useState(secondaryActive);
  const secondaryCount =
    [marketCentre, assignedAssociate].filter(Boolean).length +
    [contactMatchState, marketingStatus, listingMatch].filter((value) => value !== "ALL").length;
  const fieldClass =
    "mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-800";
  const labelClass = "text-[11px] font-semibold uppercase tracking-wide text-slate-500";
  return (
    <div className="space-y-4">
      <section className="surface-card p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))_auto] lg:items-end">
          <label className={`${labelClass} sm:col-span-2 lg:col-span-1`}>
            Search
            <input
              value={search}
              onChange={(event) => onSearch(event.target.value)}
              placeholder="Name, email, phone, listing, agent"
              className={fieldClass}
            />
          </label>
          <label className={labelClass}>
            Source
            <select
              value={source}
              onChange={(event) => onSource(event.target.value)}
              className={fieldClass}
            >
              <option value="ALL">All sources</option>
              <option value="PRIVATE_PROPERTY">Private Property</option>
              <option value="PROPERTY24">Property24</option>
              <option value="KWHOMES">KWHomes</option>
            </select>
          </label>
          <label className={labelClass}>
            From
            <input
              type="date"
              value={fromDate}
              onChange={(event) => onFrom(event.target.value)}
              className={fieldClass}
            />
          </label>
          <label className={labelClass}>
            To
            <input
              type="date"
              value={toDate}
              onChange={(event) => onTo(event.target.value)}
              className={fieldClass}
            />
          </label>
          <button
            type="button"
            aria-expanded={showMoreFilters}
            onClick={() => setShowMoreFilters((value) => !value)}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            {showMoreFilters ? "Fewer filters" : "More filters"}
            {secondaryCount ? ` (${secondaryCount})` : ""}
          </button>
        </div>
        {showMoreFilters ? (
          <div className="mt-3 grid gap-3 border-t border-slate-100 pt-3 sm:grid-cols-2 lg:grid-cols-5">
            <label className={labelClass}>
              Market Centre
              <input value={marketCentre} onChange={(event) => onMarketCentre(event.target.value)} placeholder="Name or source ID" className={fieldClass} />
            </label>
            <label className={labelClass}>
              Associate
              <input value={assignedAssociate} onChange={(event) => onAssignedAssociate(event.target.value)} placeholder="Name, email, or KWUID" className={fieldClass} />
            </label>
            <label className={labelClass}>
              Contact match
              <select value={contactMatchState} onChange={(event) => onContactMatchState(event.target.value)} className={fieldClass}>
                <option value="ALL">All states</option><option value="CANONICAL">Matched</option><option value="PROVISIONAL">Provisional</option><option value="UNMATCHED">Unmatched</option><option value="REVIEW_REQUIRED">Review required</option>
              </select>
            </label>
            <label className={labelClass}>
              Marketing status
              <select value={marketingStatus} onChange={(event) => onMarketingStatus(event.target.value)} className={fieldClass}>
                <option value="ALL">All statuses</option><option value="CLEAR">Clear</option><option value="DO_NOT_CONTACT">Do not contact</option><option value="REVIEW_REQUIRED">Review required</option>
              </select>
            </label>
            <label className={labelClass}>
              Listing match
              <select value={listingMatch} onChange={(event) => onListingMatch(event.target.value)} className={fieldClass}>
                <option value="ALL">All listings</option><option value="true">Matched</option><option value="false">Unmatched</option>
              </select>
            </label>
          </div>
        ) : null}
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Quick view
          </span>
          {(
            [
              "all",
              "today",
              "new",
              "attention",
              "unmatched-agent",
              "unmatched-listing",
            ] as QuickView[]
          ).map((view) => (
            <button
              key={view}
              type="button"
              aria-pressed={quickView === view}
              onClick={() => onQuickView(view)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${quickView === view ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
            >
              {quickViewLabel(view)}
            </button>
          ))}
          {filtersActive ? (
            <button
              type="button"
              onClick={onClearFilters}
              className="ml-auto text-xs font-semibold text-sky-700 hover:underline"
            >
              Clear Filters
            </button>
          ) : null}
        </div>
      </section>
      <section className="surface-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="font-semibold text-slate-900">Leads</h2>
            <p className="text-xs text-slate-500">
              Showing {total === 0 ? 0 : ((page - 1) * pageSize + 1).toLocaleString()}&ndash;
              {Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}{" "}
              readable leads
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
              Sort
              <select
                value={sort}
                onChange={(event) => onSort(event.target.value)}
                className="rounded-md border border-slate-300 px-2 py-1 text-sm font-normal text-slate-800"
              >
                <option value="date">Received</option>
                <option value="consumer">Consumer</option>
                <option value="source">Source</option>
                <option value="agent">Agent / Owner</option>
                <option value="price">Price</option>
              </select>
            </label>
            <button
              type="button"
              onClick={() => onDirection(direction === "desc" ? "asc" : "desc")}
              className="rounded-md border border-slate-300 px-2 py-1 text-sm"
              aria-label={`Sort direction: ${direction === "desc" ? "newest first" : "oldest first"}`}
            >
              {direction === "desc" ? "Newest first" : "Oldest first"}
            </button>
          </div>
        </div>
        {loading ? (
          <div className="p-8">
            <EmptyState
              title="Loading current lead sources"
              detail="Fetching bounded pages from the existing authenticated portal endpoints."
            />
          </div>
        ) : loadError && !leads.length ? (
          <div className="p-8">
            <EmptyState title="Lead data is unavailable" detail={loadError} />
          </div>
        ) : leads.length ? (
          <LeadTable
            leads={leads}
            onSelectLead={onSelectLead}
            onOpenContact={onOpenContact}
            onOpenListing={onOpenListing}
            onOpenMappListing={onOpenMappListing}
          />
        ) : (
          <div className="p-8">
            <EmptyState
              title="No leads match these filters"
              detail="Try clearing the search, date range, or quick view."
            />
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          <label className="flex items-center gap-2">
            Rows per page
            <select
              value={pageSize}
              onChange={(event) => onPageSize(Number(event.target.value))}
              className="rounded-md border border-slate-300 bg-white px-2 py-1"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => onPage(page - 1)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="tabular-nums">
              Page {page} of {Math.max(1, Math.ceil(total / pageSize)).toLocaleString()}
            </span>
            <button
              type="button"
              disabled={page >= Math.ceil(total / pageSize)}
              onClick={() => onPage(page + 1)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export function LeadTable({
  leads,
  onSelectLead,
  onOpenContact,
  onOpenListing,
  onOpenMappListing,
}: {
  leads: UnifiedLead[];
  onSelectLead: (lead: UnifiedLead) => void;
  onOpenContact: (lead: UnifiedLead) => void;
  onOpenListing: (lead: UnifiedLead) => void;
  onOpenMappListing: (lead: UnifiedLead) => void;
}): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1040px] table-fixed text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="w-[92px] px-3 py-2.5">Received</th>
            <th className="w-[22%] px-3 py-2.5">Consumer</th>
            <th className="w-[130px] px-3 py-2.5">Source</th>
            <th className="w-[18%] px-3 py-2.5">Listing</th>
            <th className="w-[18%] px-3 py-2.5">Agent / Owner</th>
            <th className="w-[120px] px-3 py-2.5">Contact</th>
            <th className="w-[120px] px-3 py-2.5">Marketing</th>
            <th className="w-[112px] px-3 py-2.5"><span className="sr-only">Action</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {leads.map((lead) => (
            <tr key={lead.id} className="align-top hover:bg-slate-50">
              <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                {formatCompactDate(lead.occurredAt ?? lead.receivedAt)}
                <div className="text-[11px] text-slate-400">
                  {
                    formatDate(lead.occurredAt ?? lead.receivedAt)
                      .split(",")
                      .slice(-1)[0]
                  }
                </div>
              </td>
              <td className="px-3 py-3">
                <div className="truncate font-semibold text-slate-900">
                  {lead.provisionalContactId || lead.canonicalContactId ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenContact(lead);
                      }}
                      className="text-left font-semibold text-sky-700 hover:underline"
                    >
                      {lead.consumerName ?? "Unknown consumer"}
                    </button>
                  ) : (
                    <span className="font-semibold text-slate-900">
                      {lead.consumerName ?? "Unknown consumer"}
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-slate-500">
                  {lead.email ?? lead.phone ?? "No contact detail supplied"}
                </div>
              </td>
              <td className="px-3 py-3">
                <SourceBadge source={lead.source} />
                <div className="mt-1 text-xs text-slate-500">
                  {lead.leadType ?? "Lead"}
                </div>
              </td>
              <td className="px-3 py-3">
                {isPublicKwHomesListing(
                  lead.listingAgentKwuid,
                  lead.listingReference,
                ) ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenListing(lead);
                    }}
                    className="font-medium text-sky-700 hover:underline"
                  >
                    {lead.listingReference}
                  </button>
                ) : lead.listingUrl ? (
                  <a
                    href={lead.listingUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="font-medium text-sky-700 hover:underline"
                  >
                    {lead.listingReference ?? "Open Source Listing"}
                  </a>
                ) : lead.listingInternalId ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenMappListing(lead);
                    }}
                    className="font-medium text-slate-700 hover:underline"
                  >
                    {lead.listingReference ?? "Open in MAPP"}
                  </button>
                ) : (
                  <div className="font-medium text-slate-800">
                    {lead.listingReference ?? "Not supplied"}
                  </div>
                )}
                <div className="text-xs text-slate-500">
                  {lead.suburb ?? lead.listingAddress ?? "No listing detail"}
                </div>
                {lead.listingReference && lead.listingMatchState !== "MATCHED" ? (
                  <StatusBadge label="Unmatched in MAPP" tone="warning" />
                ) : null}
              </td>
              <td className="px-3 py-3">
                <div className="truncate font-medium text-slate-800">{associateLabel(lead.agentName, lead.agentKwuid)}</div>
                <div className="truncate text-xs text-slate-500">{lead.marketCentre ?? "No Market Centre"}</div>
              </td>
              <td className="px-3 py-3">
                {(() => {
                  const link = contactLinkState(lead);
                  return <StatusBadge label={link.label} tone={link.tone} />;
                })()}
              </td>
              <td className="px-3 py-3"><StatusBadge label={lead.marketingStatus.replace(/_/g, " ")} tone={lead.marketingStatus === "DO_NOT_CONTACT" ? "danger" : lead.marketingStatus === "REVIEW_REQUIRED" || lead.marketingStatus === "UNKNOWN" ? "warning" : "good"} /></td>
              <td className="px-3 py-3">
                <button
                  type="button"
                  onClick={() => onSelectLead(lead)}
                  className="whitespace-nowrap rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-white"
                >
                  Open detail
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactsWorkspace({
  contacts,
  total,
  page,
  pageSize,
  search,
  onSearch,
  onPage,
  onPageSize,
  onOpenContact,
  filtersActive,
  onClearFilters,
}: {
  contacts: ProvisionalContact[];
  total: number;
  page: number;
  pageSize: number;
  search: string;
  onSearch: (value: string) => void;
  onPage: (value: number) => void;
  onPageSize: (value: number) => void;
  onOpenContact: (contact: ProvisionalContact) => void;
  filtersActive: boolean;
  onClearFilters: () => void;
}): JSX.Element {
  return (
    <div className="space-y-4">
      <section className="surface-card flex flex-wrap items-start justify-between gap-3 p-5">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Contacts</h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">
            Contacts are currently derived from verified lead identifiers.
            Grouping requires exact normalized email and phone matches; this is
            not the canonical contacts database.
          </p>
        </div>
        <StatusBadge label="Provisional candidates" tone="warning" />
      </section>
      <section className="surface-card flex flex-wrap items-end gap-3 p-4">
        <label className="min-w-[240px] flex-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Search
          <input
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Name, phone, email, owner"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-800"
          />
        </label>
        {filtersActive ? (
          <button
            type="button"
            onClick={onClearFilters}
            className="rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700"
          >
            Clear Filters
          </button>
        ) : null}
      </section>
      <section className="surface-card overflow-hidden">
        {contacts.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-[850px] w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-3">Name</th>
                  <th className="px-3 py-3">Phone / Email</th>
                  <th className="px-3 py-3">Agent Snapshot</th>
                  <th className="px-3 py-3">Sources</th>
                  <th className="px-3 py-3">Enquiries</th>
                  <th className="px-3 py-3">Last activity</th>
                  <th className="px-3 py-3">State</th>
                  <th className="px-3 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-slate-50">
                    <td className="px-3 py-3 font-semibold text-slate-900">
                      <button
                        type="button"
                        onClick={() => onOpenContact(contact)}
                        className="text-left font-semibold text-sky-700 hover:underline"
                      >
                        {contact.name}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-slate-600">
                      <div>{contact.phone ?? "No phone"}</div>
                      <div className="text-xs text-slate-500">
                        {contact.email ?? "No email"}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      {contact.owner ?? "Unassigned"}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-2">
                        {contact.sources.map((source) => (
                          <SourceBadge key={source} source={source} />
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-3 tabular-nums text-slate-700">
                      {contact.enquiryCount}
                    </td>
                    <td className="px-3 py-3 text-slate-600">
                      {formatDate(contact.lastActivity)}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1">
                        <StatusBadge label="Provisional" tone="warning" />
                        <StatusBadge label="Command unavailable" />
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => onOpenContact(contact)}
                        className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700"
                      >
                        Open profile
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8">
            <EmptyState
              title="No grouped contact candidates match the current filters"
              detail="A lead can exist without becoming a provisional contact. Grouping requires exact normalized email and phone values; no canonical contact or consent state is inferred here."
            />
          </div>
        )}
      </section>
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
        <label className="flex items-center gap-2">
          Rows per page
          <select
            value={pageSize}
            onChange={(event) => onPageSize(Number(event.target.value))}
            className="rounded-md border border-slate-300 px-2 py-1"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <span>
          Showing {total === 0 ? 0 : (page - 1) * pageSize + 1}&ndash;
          {Math.min(page * pageSize, total)} of {total.toLocaleString()}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
            className="rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={page >= Math.ceil(total / pageSize)}
            onClick={() => onPage(page + 1)}
            className="rounded-md border border-slate-300 px-3 py-1.5 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

void ContactsWorkspace;

function ContactProfile({
  contact,
  onClose,
  detail,
  loading,
  error,
  onRetry,
  onSelectLead,
}: {
  contact: ProvisionalContact;
  onClose: () => void;
  detail: ApiContactDetail | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onSelectLead: (lead: UnifiedLead) => void;
}): JSX.Element {
  const visibleEnquiries = detail?.enquiries.map(adaptApiLead) ?? [];
  const visibleSources = [
    ...new Set(visibleEnquiries.map((lead) => lead.source)),
  ];
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-950/30"
      role="presentation"
      onMouseDown={onClose}
    >
      <aside
        className="h-full w-full max-w-3xl overflow-y-auto bg-white p-5 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-profile-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div className="min-w-0">
            <StatusBadge label="Provisional contact candidate" tone="warning" />
            <h2
              id="contact-profile-title"
              className="mt-2 break-words text-xl font-semibold text-slate-900"
            >
              {contact.name}
            </h2>
            <p className="mt-1 break-all text-xs text-slate-500">
              Candidate ID: {contact.id}
            </p>
            <p className="mt-2 max-w-xl text-sm text-slate-600">
              This person has been grouped from strong identifiers in existing lead data but has not yet been written to the canonical contacts table.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
        <div className="mt-5 space-y-5">
          <DrawerSection title="Overview">
            <DetailGrid
              items={[
                ["Phone", contact.phone ?? "Not supplied"],
                ["Email", contact.email ?? "Not supplied"],
                ["Candidate ID", contact.id],
                ["First activity", formatDate(detail?.first_seen_at ?? null)],
                ["Last activity", formatDate(detail?.last_seen_at ?? contact.lastActivity)],
                [
                  "Sources",
                  (visibleSources.length ? visibleSources : contact.sources)
                    .map(sourceLabelFor)
                    .join(", "),
                ],
                ["Enquiries", String(detail?.enquiry_count ?? contact.enquiryCount)],
              ]}
            />
          </DrawerSection>
          <DrawerSection title="Enquiries">
            {loading ? <p className="text-sm text-slate-500">Loading...</p> : error ? (
              <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-rose-700">
                <span>Error loading section: {error}</span>
                <button type="button" onClick={onRetry} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700">Retry</button>
              </div>
            ) : detail?.enquiries.length ? (
              <div className="divide-y divide-slate-100">
                {detail.enquiries.map((apiLead) => {
                  const enquiry = adaptApiLead(apiLead);
                  return (
                    <button
                      key={enquiry.id}
                      type="button"
                      onClick={() => onSelectLead(enquiry)}
                      className="block w-full min-w-0 py-3 text-left first:pt-0 last:pb-0 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-600"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <span className="font-semibold text-sky-700">
                          {enquiry.sourceLabel} · {leadTypeLabel(enquiry.leadType)}
                        </span>
                        <span className="text-xs text-slate-500">
                          {formatDate(enquiry.occurredAt ?? enquiry.receivedAt)}
                        </span>
                      </div>
                      <div className="mt-1 break-words text-sm font-medium text-slate-800">
                        {enquiry.listingReference ?? "No listing"}
                      </div>
                      <div className="mt-1 break-words text-xs text-slate-500">{enquiry.agentName ?? "Unmatched agent"} · {enquiry.marketCentre ?? "No Market Centre"}</div>
                      <span className="mt-1 block break-all text-[11px] text-slate-400">Provider lead ID: {enquiry.sourceLeadId}</span>
                      {enquiry.message ? (
                        <div className="mt-2 break-words text-xs leading-5 text-slate-500">
                          {enquiry.message}
                        </div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : <p className="text-sm text-slate-500">No enquiries</p>}
          </DrawerSection>
          <DrawerSection title="Associated Agents">
            {loading ? <p className="text-sm text-slate-500">Loading...</p> : error ? <p className="text-sm text-rose-700">Error loading section</p> : detail?.associatedAgents.length ? (
              <div className="divide-y divide-slate-100">
                {detail.associatedAgents.map((agent) => (
                  <div
                    key={agent.name ?? "unassigned"}
                    className="flex flex-col gap-1 py-3 text-sm first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
                  >
                    <span className="min-w-0">
                      <span className="block break-words font-semibold text-slate-900">{associateLabel(agent.name, agent.kwuid)}</span>
                      <span className="mt-0.5 block text-xs text-slate-500">{agent.marketCentre ?? "Market Centre not recorded"}</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-slate-500">
                      {agent.enquiryCount}{" "}
                      {agent.enquiryCount === 1 ? "enquiry" : "enquiries"}
                    </span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">No associated agents</p>}
          </DrawerSection>
          <DrawerSection title="Assignments">
            <EmptyState
              compact
              title="No canonical assignment history"
              detail="Assignment history will be available when the canonical contacts data connection is live."
            />
          </DrawerSection>
          <DrawerSection title="Command">
            <EmptyState
              compact
              title="Command integration has not been connected"
              detail="Command mapping, sync, tags, and activities are intentionally inactive until its approved authorization flow exists."
            />
          </DrawerSection>
          <DrawerSection title="Consent">
            <EmptyState
              compact
              title="No consent records available yet"
              detail="This read-only facade does not infer consent from portal lead data."
            />
          </DrawerSection>
          <DrawerSection title="DNC">
            <EmptyState
              compact
              title="No DNC records recorded in MAPP yet"
              detail="This read-only facade does not infer DNC state from portal lead data."
            />
          </DrawerSection>
          <DrawerSection title="Activity">
            <EmptyState
              compact
              title="No canonical activity timeline"
              detail="Only the current enquiry timestamps are available in this facade."
            />
          </DrawerSection>
          <DrawerSection title="More / Audit">
            <EmptyState
              compact
              title="No canonical audit history"
              detail="Audit history will appear once the canonical data connection is active."
            />
          </DrawerSection>
        </div>
      </aside>
    </div>
  );
}

function PausedCommandWorkspace(): JSX.Element {
  return (
    <PlaceholderPanel
      title="Command CRM"
      detail="Paused pending KWW confirmation. No Command OAuth, contact reads, sync, submissions, tags, SmartPlans, or writes are active. Once connected, every valid lead assigned to an associate will sync automatically to that associate's Command account — including leads whose identity is still in data quality review, using the best resolved contact. There is no approval step."
      label="PAUSED"
    />
  );
}

function CommandWorkspacePrototype({
  token,
  activeContextId,
}: {
  token: string | null;
  activeContextId: string;
}): JSX.Element {
  const [active, setActive] = useState("Overview");
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);
  const [contacts, setContacts] = useState<Array<Record<string, unknown>>>([]);
  const [message, setMessage] = useState<string | null>(null);
  const items = [
    "Overview",
    "Connections",
    "Contact Sync",
    "Lead Submissions",
    "Failures",
    "Activity",
  ];
  useEffect(() => {
    if (!token || !activeContextId) return;
    const headers = {
      Authorization: `Bearer ${token}`,
      "X-Active-Context": activeContextId,
    };
    void apiFetch("/api/command/status", { headers })
      .then((response) => response.json())
      .then((body) => setStatus(body as Record<string, unknown>))
      .catch(() => setMessage("Command status is unavailable."));
  }, [activeContextId, token]);

  const connection = status?.connection as Record<string, unknown> | undefined;
  const connected = connection?.status === "CONNECTED";
  const configurationReady = status?.configurationReady === true;
  const readinessLabel = !status
    ? "CHECKING"
    : !configurationReady
      ? "NOT CONFIGURED"
      : String(connection?.status ?? "NOT_CONNECTED").replace(/_/g, " ");
  const request = async (
    path: string,
    init?: RequestInit,
  ): Promise<Record<string, unknown>> => {
    const response = await apiFetch(path, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Active-Context": activeContextId,
        ...(init?.headers ?? {}),
      },
    });
    const body = (await response.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;
    if (!response.ok)
      throw new Error(
        typeof body.error === "string" ? body.error : "Command request failed.",
      );
    return body;
  };
  const connect = async (): Promise<void> => {
    try {
      const body = await request("/api/command/connect", { method: "POST" });
      if (typeof body.authorizationUrl === "string")
        window.location.assign(body.authorizationUrl);
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Command connection could not start.",
      );
    }
  };
  const testConnection = async (): Promise<void> => {
    try {
      await request("/api/command/test-connection", { method: "POST" });
      setMessage("Command connection verified.");
      const refreshed = await request("/api/command/status");
      setStatus(refreshed);
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Command connection test failed.",
      );
    }
  };
  const disconnect = async (): Promise<void> => {
    if (
      !window.confirm(
        "Disconnect this local Command connection? No Command contacts will be changed.",
      )
    )
      return;
    try {
      await request("/api/command/disconnect", { method: "POST" });
      setMessage("Command disconnected.");
      const refreshed = await request("/api/command/status");
      setStatus(refreshed);
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Command disconnect failed.",
      );
    }
  };
  const loadContacts = async (): Promise<void> => {
    try {
      const body = await request("/api/command/contacts?max=25&offset=0");
      setContacts(
        Array.isArray(body.rows)
          ? (body.rows as Array<Record<string, unknown>>)
          : [],
      );
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Command contacts could not be read.",
      );
    }
  };

  return (
    <div className="space-y-4">
      <SubTabs items={items} active={active} onChange={setActive} />
      {message ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {message}
        </div>
      ) : null}
      {active === "Contact Sync" ? (
        <section className="surface-card p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Command contacts
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Read-only verification page limited to 25 contacts.
              </p>
            </div>
            <button
              type="button"
              disabled={!connected}
              onClick={loadContacts}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold disabled:opacity-40"
            >
              Read Contacts
            </button>
          </div>
          {contacts.length ? (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Phone</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {contacts.map((contact) => (
                    <tr key={String(contact.commandContactId)}>
                      <td className="px-3 py-3">
                        {[contact.firstName, contact.lastName]
                          .filter(Boolean)
                          .join(" ") || "Unnamed"}
                      </td>
                      <td className="px-3 py-3">
                        {Array.isArray(contact.phones)
                          ? contact.phones.join(", ")
                          : ""}
                      </td>
                      <td className="px-3 py-3">
                        {Array.isArray(contact.emails)
                          ? contact.emails.join(", ")
                          : ""}
                      </td>
                      <td className="px-3 py-3">
                        {String(contact.source ?? "Not supplied")}
                      </td>
                      <td className="px-3 py-3">
                        {String(contact.updatedAt ?? "Not supplied")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-5">
              <EmptyState
                title="No Command contacts loaded"
                detail={
                  connected
                    ? "Use Read Contacts to request a bounded read-only page."
                    : "Connect and verify Command before reading contacts."
                }
              />
            </div>
          )}
        </section>
      ) : active === "Overview" || active === "Connections" ? (
        <section className="surface-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Command CRM
              </h2>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">
                Securely connect your KW Command account to MAPP. This Stage 3A
                pilot is read-only; no contacts will be changed.
              </p>
            </div>
            <StatusBadge
              label={readinessLabel}
              tone={connected ? "good" : "neutral"}
            />
          </div>
          <dl className="mt-5 grid gap-3 sm:grid-cols-2">
            <DetailGrid
              items={[
                [
                  "Associate",
                  String(
                    (status?.associate as Record<string, unknown> | undefined)
                      ?.kwuid ?? "Not available",
                  ),
                ],
                [
                  "Organization",
                  String(connection?.commandOrgId ?? "Not available"),
                ],
                [
                  "Granted permissions",
                  Array.isArray(connection?.grantedScopes)
                    ? (connection?.grantedScopes as unknown[]).join(", ")
                    : "Not available",
                ],
                [
                  "Connected at",
                  String(connection?.connectedAt ?? "Not available"),
                ],
                [
                  "Last verified",
                  String(connection?.lastVerifiedAt ?? "Not available"),
                ],
                [
                  "Last contact read",
                  String(connection?.lastContactReadAt ?? "Not available"),
                ],
              ]}
            />
          </dl>
          <div className="mt-5 flex flex-wrap gap-2">
            {connected ? (
              <>
                <button
                  type="button"
                  onClick={testConnection}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold"
                >
                  Test Connection
                </button>
                <button
                  type="button"
                  onClick={disconnect}
                  className="rounded-md border border-rose-300 px-3 py-2 text-sm font-semibold text-rose-700"
                >
                  Disconnect Command
                </button>
              </>
            ) : (
              <button
                type="button"
                disabled={!configurationReady || !status?.pilotAllowed}
                onClick={connect}
                className="rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
              >
                Connect Command
              </button>
            )}
          </div>
          {!configurationReady ? (
            <p className="mt-3 text-xs text-slate-500">
              Command connection setup is not complete.
            </p>
          ) : status?.pilotAllowed === false ? (
            <p className="mt-3 text-xs text-slate-500">
              Command connection will be enabled after pilot validation.
            </p>
          ) : null}
        </section>
      ) : (
        <PlaceholderPanel
          title={`Command CRM / ${active}`}
          detail="This Stage 3A surface is read-only. Command writes, submissions, SmartPlans, tags, and custom-field changes are not available."
          label="Read-only"
        />
      )}
    </div>
  );
}

void CommandWorkspacePrototype;

function ComplianceWorkspace(): JSX.Element {
  const [active, setActive] = useState("Overview");
  const items = [
    "Overview",
    "Consent",
    "Marketing Eligibility",
    "Exceptions",
    "Complaints",
    "Audit",
  ];
  return (
    <div className="space-y-4">
      <SubTabs items={items} active={active} onChange={setActive} />
      {active === "Audit" ? <DncAuditLog /> : (
        <PlaceholderPanel
          title={`Compliance / ${active}`}
          detail="Consent, marketing eligibility, exceptions, complaints, and audit evidence will be connected to the canonical data foundation in a later phase."
          label="No canonical data"
        />
      )}
    </div>
  );
}

function IntegrationsWorkspace({
  sourceStates,
  integrationStatus,
}: {
  sourceStates: SourceState[];
  integrationStatus: Record<string, unknown> | null;
}): JSX.Element {
  const [active, setActive] = useState("Overview");
  const items = [
    "Overview",
    "Property24",
    "Private Property",
    "KWHomes",
    "Command",
    "Job Queue",
  ];
  const statusRecord = integrationStatus?.[
    active === "Property24"
      ? "property24"
      : active === "Private Property"
        ? "privateProperty"
        : active === "KWHomes"
          ? "kwhomes"
          : ""
  ] as
    | {
        state?: string;
        lastLeadAt?: string | null;
        total?: number;
        failedEmailDeliveries?: number;
      }
    | undefined;
  return (
    <div className="space-y-4">
      <SubTabs items={items} active={active} onChange={setActive} />
      {active === "Overview" ? (
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {sourceStates.map((state) => {
            const key =
              state.source === "PROPERTY24"
                ? "property24"
                : state.source === "PRIVATE_PROPERTY"
                  ? "privateProperty"
                  : "kwhomes";
            const sourceStatus = integrationStatus?.[key] as
              | { state?: string; total?: number; lastLeadAt?: string | null }
              | undefined;
            return (
              <section key={state.source} className="surface-card p-5">
                <div className="flex items-start justify-between gap-2">
                  <SourceBadge source={state.source} />
                  <StatusBadge
                    label={sourceStatus?.state ?? "UNKNOWN"}
                    tone={
                      sourceStatus?.state === "AVAILABLE" ? "info" : "neutral"
                    }
                  />
                </div>
                <div className="mt-5 text-sm text-slate-600">
                  {sourceStatus?.total !== undefined
                    ? `${sourceStatus.total} stored rows`
                    : "No local status evidence."}
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  Last lead:{" "}
                  {sourceStatus?.lastLeadAt
                    ? formatDate(sourceStatus.lastLeadAt)
                    : "Not available"}
                </div>
              </section>
            );
          })}
        </section>
      ) : active === "Job Queue" ? (
        <PlaceholderPanel
          title="Integrations / Job Queue"
          detail="The canonical integration_jobs table is not live, so pending, retry, failed, and dead job counts are not available."
          label="NOT_AVAILABLE"
        />
      ) : (
        <PlaceholderPanel
          title={`Integrations / ${active}`}
          detail={
            statusRecord?.state === "AVAILABLE"
              ? `Local database evidence is available. Last lead: ${statusRecord.lastLeadAt ? formatDate(statusRecord.lastLeadAt) : "not available"}.`
              : "No local status evidence is available for this source."
          }
          label={statusRecord?.state ?? "UNKNOWN"}
        />
      )}
    </div>
  );
}
